import { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type { ShopifyOrderView, ShopifyShopFit } from "../../api/types";
import { ReasonModal } from "../ui/ReasonModal";
import { whenText } from "./report/webOrdersText";
import { dueDayText, orderLineSummary } from "./webOrderDates";

/** How a shop stands for the order on today's stock — never a promise about the preparation day. */
function fitText(shop: ShopifyShopFit): string {
  if (shop.fit === "ALL") return "Has everything today";
  if (shop.fit === "TRANSFER") return `Today: bring ${shop.missing.join(", ")}`;
  return `Short today: ${shop.missing.join("; ")}`;
}

/**
 * G-167 (OD-283, mockup approved 2026-10-22 as OD-288) — the owner or an
 * admin passes an order a shop has to another shop, with a reason. The
 * server refuses once a bouquet is built, for a pickup, and while a courier
 * job is open; its refusal is shown inside the dialog.
 */
export function PassWebOrderModal({
  order,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  order: ShopifyOrderView;
  busy: boolean;
  error: string | null;
  onConfirm: (locationId: string, reason: string) => void;
  onClose: () => void;
}) {
  const [shops, setShops] = useState<ShopifyShopFit[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getShopifyOrderShopFit(order.id)
      .then((rows) => {
        if (!live) return;
        const others = rows.filter((row) => !row.current);
        setShops(others);
        setTarget((current) => current ?? others.find((row) => row.fit === "ALL")?.locationId ?? others[0]?.locationId ?? null);
      })
      .catch((e: unknown) => {
        if (live) setLoadError(e instanceof Error ? e.message : "Could not load the shops");
      });
    return () => {
      live = false;
    };
  }, [order.id]);

  const targetName = shops?.find((shop) => shop.locationId === target)?.name;
  const label = order.orderNumber ?? order.shopifyOrderId;
  const day = dueDayText(order);
  const placed = [
    order.claimedLocation?.name,
    order.assigned ? `assigned by ${order.assigned.by?.fullName ?? "the office"} · ${whenText(order.assigned.at)}` : null,
    order.lastPass ? `passed by ${order.lastPass.by.fullName} · ${whenText(order.lastPass.at)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <ReasonModal
      title="Pass to another shop"
      message={[label, orderLineSummary(order), day ? `for ${day}` : null].filter(Boolean).join(" · ")}
      reasonMode="required"
      reasonLabel="Reason"
      confirmLabel={targetName ? `Pass to ${targetName}` : "Pass"}
      busy={busy}
      error={error ?? loadError ?? pickError}
      onConfirm={(reason) => {
        if (!target) {
          setPickError("Choose the shop to pass it to");
          return;
        }
        if (reason) onConfirm(target, reason);
      }}
      onClose={onClose}
    >
      <p className="muted wo-pass-placed">{placed}</p>
      <fieldset className="wo-skip-reasons">
        <legend>To</legend>
        {shops === null && !loadError ? <p className="muted">Loading…</p> : null}
        {shops?.length === 0 ? <p className="muted">No other open shop</p> : null}
        {(shops ?? []).map((shop) => (
          <label
            key={shop.locationId}
            className={shop.locationId === target ? "wo-skip-option wo-pass-option is-selected" : "wo-skip-option wo-pass-option"}
          >
            <input
              type="radio"
              name="pass-shop"
              value={shop.locationId}
              checked={shop.locationId === target}
              onChange={() => {
                setTarget(shop.locationId);
                setPickError(null);
              }}
            />
            <span>
              <strong>{shop.name}</strong>
              <span className={`wo-pass-fit is-${shop.fit.toLowerCase()}`}>{fitText(shop)}</span>
            </span>
          </label>
        ))}
      </fieldset>
    </ReasonModal>
  );
}
